/**
 * Persistent frame cache for scrub / buffering fallback.
 *
 * Two tiers:
 *   1. In-memory LRU of decoded `ImageBitmap`s (fast, bounded) — for instant
 *      redraws while scrubbing.
 *   2. Cache API store of the raw JPEG `Response`s (cross-session persistence)
 *      — surviving reloads so re-scrubbing a region is free.
 *
 * Frames come from pandora's `{site}/{item}/{res}p{t}.jpg` endpoint (see
 * `frameUrlForCanvas`). `t` is CONCATENATED item time; pandora serves the
 * nearest extracted frame and never 404s. Times are quantized to a coarse grid
 * so nearby scrub positions collapse onto shared cache keys.
 */
import type { Archive } from '../api/archives';
import { frameUrlForCanvas } from './mediaurls';

const CACHE_NAME = 'fabled-frames-v1';
const MEM_MAX = 240; // decoded bitmaps kept in memory
const DEFAULT_GRID = 0.5; // seconds
const DEFAULT_TARGET_RES = 240; // preview-frame target height

/** Round a frame time onto a coarse grid so nearby scrub positions share a key. */
export function quantizeFrameTime(t: number, grid = DEFAULT_GRID): number {
  const clamped = Math.max(0, t);
  if (!(grid > 0)) return clamped;
  const q = Math.round(clamped / grid) * grid;
  // strip float noise (e.g. 0.30000000000000004)
  return Math.round(q * 1000) / 1000;
}

/** Stable cache key for a quantized frame. */
export function frameCacheKey(archiveKey: string, item: string, res: number, t: number): string {
  return `${archiveKey}:${item}:${res}:${t}`;
}

/**
 * Pick a preview-frame resolution: the smallest available that is >= target
 * (good enough to look decent, small enough to fetch fast), else the largest.
 */
export function pickFrameResolution(resolutions: number[], target = DEFAULT_TARGET_RES): number {
  if (!resolutions.length) return target;
  const sorted = [...resolutions].sort((a, b) => a - b);
  for (const r of sorted) if (r >= target) return r;
  return sorted[sorted.length - 1];
}

/**
 * Bounded LRU map. Recency = Map insertion order; `get`/`set` move the key to
 * the most-recently-used end and the oldest key is evicted past `max`.
 */
export class Lru<V> {
  private map = new Map<string, V>();
  constructor(
    private max: number,
    private onEvict?: (v: V) => void,
  ) {}

  get size(): number {
    return this.map.size;
  }
  has(k: string): boolean {
    return this.map.has(k);
  }
  get(k: string): V | undefined {
    const v = this.map.get(k);
    if (v !== undefined) {
      this.map.delete(k);
      this.map.set(k, v);
    }
    return v;
  }
  set(k: string, v: V): void {
    if (this.map.has(k)) this.map.delete(k);
    this.map.set(k, v);
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value as string;
      const ev = this.map.get(oldest) as V;
      this.map.delete(oldest);
      this.onEvict?.(ev);
    }
  }
  keys(): string[] {
    return [...this.map.keys()];
  }
  clear(): void {
    if (this.onEvict) for (const v of this.map.values()) this.onEvict(v);
    this.map.clear();
  }
}

export interface FrameOpts {
  /** Frame height to request; defaults to `pickFrameResolution(archive.resolutions)`. */
  resolution?: number;
  /** Time-quantization grid in seconds; defaults to 0.5s. */
  grid?: number;
}

export class FrameCache {
  // NB: no eager ImageBitmap.close() on eviction — the preview engine may still
  // hold a reference to the last-shown frame for a source; GC reclaims evicted
  // bitmaps. Bound keeps decoded memory in check.
  private mem = new Lru<ImageBitmap>(MEM_MAX);
  private inflight = new Map<string, Promise<ImageBitmap | null>>();
  private cacheStore: Promise<Cache | null> | null = null;

  private keyFor(archive: Archive, item: string, t: number, opts: FrameOpts): { res: number; qt: number; key: string } {
    const grid = opts.grid ?? DEFAULT_GRID;
    const res = opts.resolution ?? pickFrameResolution(archive.resolutions);
    const qt = quantizeFrameTime(t, grid);
    return { res, qt, key: frameCacheKey(archive.key, item, res, qt) };
  }

  /** Synchronous peek — returns a decoded bitmap only if already in memory. */
  peek(archive: Archive, item: string, t: number, opts: FrameOpts = {}): ImageBitmap | null {
    const { key } = this.keyFor(archive, item, t, opts);
    return this.mem.get(key) ?? null;
  }

  /** Get-or-fetch a decoded frame bitmap; resolves null on any failure. */
  async getFrame(
    archive: Archive,
    item: string,
    t: number,
    opts: FrameOpts = {},
  ): Promise<ImageBitmap | null> {
    const { res, qt, key } = this.keyFor(archive, item, t, opts);
    const cached = this.mem.get(key);
    if (cached) return cached;
    const existing = this.inflight.get(key);
    if (existing) return existing;

    const url = frameUrlForCanvas(archive, item, qt, res);
    const p = this.fetchAndDecode(url)
      .then((bmp) => {
        if (bmp) this.mem.set(key, bmp);
        this.inflight.delete(key);
        return bmp;
      })
      .catch(() => {
        this.inflight.delete(key);
        return null;
      });
    this.inflight.set(key, p);
    return p;
  }

  clearMemory(): void {
    this.mem.clear();
  }

  private async fetchAndDecode(url: string): Promise<ImageBitmap | null> {
    try {
      const blob = await this.fetchBlob(url);
      if (!blob || typeof createImageBitmap === 'undefined') return null;
      return await createImageBitmap(blob);
    } catch {
      return null;
    }
  }

  private async fetchBlob(url: string): Promise<Blob | null> {
    const store = await this.openCache();
    if (store) {
      const hit = await store.match(url);
      if (hit) return await hit.blob();
      // Public media JPEGs: fetch WITHOUT credentials. Archives serve these with
      // `Access-Control-Allow-Origin: *`, and the browser rejects a wildcard ACAO
      // on a credentialed request — so `credentials: 'include'` would break CORS.
      const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
      if (!res.ok) return null;
      try {
        await store.put(url, res.clone());
      } catch {
        // quota / opaque response — persistence best-effort
      }
      return await res.blob();
    }
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) return null;
    return await res.blob();
  }

  private openCache(): Promise<Cache | null> {
    if (this.cacheStore) return this.cacheStore;
    this.cacheStore = (async () => {
      try {
        if (typeof caches === 'undefined') return null;
        return await caches.open(CACHE_NAME);
      } catch {
        return null;
      }
    })();
    return this.cacheStore;
  }
}

let singleton: FrameCache | null = null;

/** Process-wide frame cache (shared by every preview engine instance). */
export function getFrameCache(): FrameCache {
  if (!singleton) singleton = new FrameCache();
  return singleton;
}
